import { exploreWireframeDegreeIndex, SERENITY_TONIC_FILL } from "./scaleModuleTheme";

/**
 * Estilo de pentagrama unificado (Canciones / Escalas / Acordes):
 * lienzo transparente, líneas y notación claras (incl. plicas y beams).
 */

/** Halos Serenity por grado (Explore pentagrama). */
export const SERENITY_STAFF_HALO_RGB = [
  "rgba(248, 113, 113, 0.85)", // R
  "rgba(196, 181, 253, 0.8)", // II
  "rgba(167, 243, 208, 0.8)", // III
  "rgba(103, 232, 249, 0.75)", // IV
  "rgba(253, 186, 116, 0.8)", // V
  "rgba(191, 219, 254, 0.75)", // VI
  "rgba(216, 180, 254, 0.75)", // VII
] as const;

export const SERENITY_STAFF_STROKE_RGB = [
  "rgba(248, 113, 113, 0.9)",
  "rgba(196, 181, 253, 0.85)",
  "rgba(167, 243, 208, 0.85)",
  "rgba(103, 232, 249, 0.8)",
  "rgba(253, 186, 116, 0.85)",
  "rgba(191, 219, 254, 0.8)",
  "rgba(216, 180, 254, 0.8)",
] as const;

/** Explore sandbox: pentagrama como marca de agua teórica */
export const PRACTICE_STAFF_COLORS_ATMOSPHERIC = {
  background: "transparent",
  staffLine: "rgba(196, 181, 253, 0.5)",
  notation: "rgba(255, 255, 255, 0.3)",
  rest: "rgba(196, 181, 253, 0.15)",
  brace: "rgba(196, 181, 253, 0.45)",
  activeFill: "rgba(255, 255, 255, 0.85)",
  activeStroke: SERENITY_TONIC_FILL,
  highlightStroke: "rgba(196, 181, 253, 0.65)",
  legendMuted: "rgba(148, 163, 184, 0.5)",
} as const;

export function serenityStaffDropShadow(degree: string, active = true): string {
  const idx = exploreWireframeDegreeIndex(degree);
  const rgb = SERENITY_STAFF_HALO_RGB[idx % 7];
  const blur = active ? 12 : 8;
  return `drop-shadow(0 0 ${blur}px ${rgb})`;
}

export function serenityStaffStrokeForDegree(degree: string): string {
  const idx = exploreWireframeDegreeIndex(degree);
  if (degree === "R") return SERENITY_TONIC_FILL;
  return SERENITY_STAFF_STROKE_RGB[idx % 7];
}

/** Halo Serenity — mástil Explore (filtro SVG / CSS en nodo). */
export const SERENITY_NECK_ACTIVE_FILL = "#34d399";
export const SERENITY_NECK_HOVER_FILL = "#c084fc";
export const SERENITY_NECK_ACTIVE_FILTER =
  "drop-shadow(0 0 30px rgba(52, 211, 153, 0.8))";
export const SERENITY_NECK_HOVER_FILTER =
  "drop-shadow(0 0 22px rgba(192, 132, 252, 0.75))";

export const PRACTICE_STAFF_COLORS = {
  background: "transparent",
  staffLine: "rgba(240, 243, 245, 0.65)",
  notation: "#F0F3F5",
  rest: "rgba(240, 243, 245, 0.38)",
  brace: "rgba(240, 243, 245, 0.55)",
  activeFill: "#F59E0B",
  activeStroke: "#D97706",
  highlightStroke: "#FBBF24",
  legendMuted: "rgba(148, 163, 184, 0.9)",
} as const;

export const PRACTICE_STAFF_CONTAINER_STYLE = {
  background: PRACTICE_STAFF_COLORS.background,
} as const;

type VexContext = {
  setStrokeStyle(style: string): void;
  setFillStyle(style: string): void;
  setLineWidth(width: number): void;
};

/** Colorea líneas del pentagrama, clave y compás antes de `stave.draw()`. */
export function paintStaveLines(
  ctx: VexContext,
  palette: typeof PRACTICE_STAFF_COLORS = PRACTICE_STAFF_COLORS
): void {
  ctx.setStrokeStyle(palette.staffLine);
  ctx.setFillStyle(palette.staffLine);
}

export function notationNoteStyle(
  opts?: {
    active?: boolean;
    highlighted?: boolean;
    selected?: boolean;
    degree?: string;
    atmospheric?: boolean;
  },
  palette: typeof PRACTICE_STAFF_COLORS = PRACTICE_STAFF_COLORS
): { fillStyle: string; strokeStyle: string; lineWidth?: number } {
  if (opts?.active) {
    if (opts.atmospheric && opts.degree) {
      return {
        fillStyle: PRACTICE_STAFF_COLORS_ATMOSPHERIC.activeFill,
        strokeStyle: serenityStaffStrokeForDegree(opts.degree),
        lineWidth: 2,
      };
    }
    return {
      fillStyle: palette.activeFill,
      strokeStyle: palette.activeStroke,
      lineWidth: 2,
    };
  }
  if (opts?.selected) {
    return {
      fillStyle: palette.notation,
      strokeStyle: palette.highlightStroke,
      lineWidth: 2.5,
    };
  }
  if (opts?.highlighted) {
    return {
      fillStyle: palette.notation,
      strokeStyle: palette.highlightStroke,
      lineWidth: 2,
    };
  }
  return {
    fillStyle: palette.notation,
    strokeStyle: palette.notation,
    lineWidth: 1,
  };
}

export function styleBeamLikeSong(beam: { setStyle(style: object): void }): void {
  beam.setStyle({
    fillStyle: PRACTICE_STAFF_COLORS.notation,
    strokeStyle: PRACTICE_STAFF_COLORS.notation,
  });
}
